/**
 * Hooks para Parámetros del sistema — Fase 8
 *
 * Patrón #014: lectura directa a Supabase. La RLS en `parametros`
 * (migration inicial) permite SELECT a authenticated y UPDATE solo a
 * admin_general.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Parametro } from '../types/parametro';

const QK = {
  all: ['parametros'] as const,
};

// ── LIST ─────────────────────────────────────────────────────────────────────

export function useParametros() {
  return useQuery({
    queryKey: QK.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parametros')
        .select('*')
        .order('clave');
      if (error) throw error;
      return (data ?? []) as Parametro[];
    },
    staleTime: 60_000,
  });
}

// ── UPDATE (solo valor + descripcion, nunca clave ni tipo) ──────────────────

export function useUpdateParametro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clave,
      valor,
      descripcion,
    }: {
      clave: string;
      valor: string;
      descripcion?: string | null;
    }) => {
      // Construir payload solo con los campos que se actualizan
      const payload: { valor: string; descripcion?: string | null } = { valor };
      if (descripcion !== undefined) payload.descripcion = descripcion;

      const { data, error } = await supabase
        .from('parametros')
        .update(payload)
        .eq('clave', clave)
        .select('*')
        .single();
      if (error) throw error;
      return data as Parametro;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.all });
    },
  });
}
